/**
 * @format
 */

import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api.ts';

function MultiSelectAutocomplete({
  endpoint,
  getOptionLabel,
  isOptionEqualToValue,
  name,
  label,
  onChange,
  value,
  disabled,
  error,
  helperText,
  required,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const fetchOptions = () => {
    setLoading(true);
    api
      .get(endpoint(''))
      .then((result) => {
        setOptions(result.data.results || []);
      })
      .catch((err) => {
        console.error('Failed to load options:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      fetchOptions();
    }
  }, [open]);

  const isSelected = (option) =>
    value.some((v) => isOptionEqualToValue(option, v));

  const handleToggle = (option) => {
    const isCurrentlySelected = isSelected(option);
    let newValue;

    if (isCurrentlySelected) {
      newValue = value.filter((v) => !isOptionEqualToValue(option, v));
    } else {
      newValue = [...value, option];
    }

    if (onChange) {
      onChange(null, newValue);
    }
  };

  const handleDelete = (optionToDelete) => {
    const newValue = value.filter(
      (v) => !isOptionEqualToValue(optionToDelete, v),
    );
    if (onChange) {
      onChange(null, newValue);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <FormControl fullWidth error={error} required={required}>
        <InputLabel
          shrink
          sx={{ backgroundColor: 'background.paper', px: 0.5 }}
        >
          {label}
        </InputLabel>
        <Box
          ref={anchorRef}
          onClick={() => !disabled && setOpen(!open)}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            minHeight: 56,
            px: 1.5,
            py: 1,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            backgroundColor: 'background.paper',
          }}
        >
          {value.map((v, index) => (
            <Chip
              key={v.id || v.slug || index}
              label={getOptionLabel(v)}
              size="small"
              onDelete={disabled ? undefined : () => handleDelete(v)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          ))}
        </Box>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{
            width: anchorRef.current?.offsetWidth,
            zIndex: 1300,
          }}
        >
          <Paper elevation={8} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && options.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                Aucun résultat trouvé
              </Box>
            )}
            {!loading && options.length > 0 && (
              <List dense>
                {options.map((option, index) => (
                  <ListItemButton
                    key={option.id || option.slug || index}
                    onClick={() => handleToggle(option)}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected(option)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={getOptionLabel(option)} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </FormControl>
    </ClickAwayListener>
  );
}

MultiSelectAutocomplete.propTypes = {
  endpoint: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  isOptionEqualToValue: PropTypes.func.isRequired,
  name: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.arrayOf(PropTypes.shape()),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
};

MultiSelectAutocomplete.defaultProps = {
  name: null,
  label: null,
  onChange: null,
  value: [],
  disabled: false,
  error: false,
  helperText: null,
  required: false,
};

export default MultiSelectAutocomplete;
